import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ZOOM_CHANGE,
  ZOOM_HOVER,
  ZoomButtonHoverAction,
  ZoomChangeAction
} from "../../actions/actionTypes";
import { MainStore } from "../../store";
import {
  Container,
  SliderRailingContainer,
  ZoomIcon,
  ZoomContainer,
  ZoomButtons
} from "./styles";
import { getZoomProportion } from "../../util/zoom_levels";
import { seconds_to_pixels } from "../../util/time_position_conversion";
import { theme } from "../../../globalStyles";
import Icon from "../../../components/Icon";
import { EditorContext } from "../../context/EditorContext/EditorProvider";

export const MIN_ZOOM_LEVEL = 1;
export const MAX_ZOOM_LEVEL = 5;

export function ZoomControls() {
  const [zoomSliderVisible, setZoomSliderVisible] = useState(false);

  const dispatch = useDispatch();
  const zoomLevel = useSelector<MainStore, number>(
    state => state.uiState.zoomLevel
  );
  const locatorTime = useSelector<MainStore, number>(
    state => state.editorState.locatorTime
  );
  const scrollX = useSelector<MainStore, number>(
    state => state.uiState.projectScrollX
  );
  const trackDurationSeconds = useSelector<MainStore, number>(
    state => state.project.customMixInfo.length
  );
  const outMarkerTime = useSelector<MainStore, number>(
    state => state.project.markers.out.songTime
  );
  const zoomProportion = getZoomProportion(zoomLevel);

  const editor = useContext(EditorContext);

  const zoomHover = useSelector<MainStore, boolean>(
    state => state.uiState.zoomHover
  );

  const onChange = (newZoomLevel: number) => {
    setZoomLevel(newZoomLevel);
  };

  useEffect(() => {
    setZoomSliderVisible(zoomHover);
  }, [zoomHover]);

  /*
   * Performing zoom while maintaining scroll position gets tricky
   * particularly when a zoom out happens and the current pixel scroll position
   * would overflow the container and clamps to the end scroll position instead
   * of maintaining proportion. Ex., you're scrolled 60% through the viewport,
   * then zoom out, it would quickly flash the content at the end of the viewport,
   * then snap to 60% scroll in the viewport making for poor UX. The algorithm here
   * is to manually elongate or shrink the viewport content and perform the
   * proportional scroll prior to zooming. So basically, we're performing the scroll
   * action before the zoom, making sure the correct container scrollLeft is set to
   * the correct position such that when the zoom occurs it's in the correct place
   * already.
   *
   * The logic basically is zoom to the center of the screen if the locator is
   * outside the viewport and zoom towards the locator if it is not
   */
  function setZoomLevel(newZoomLevel: number) {
    // handle case where zoom rc-slider is focused and user switches tab and comes back
    if (newZoomLevel === 0) return;

    const maxSongTime = Math.max(outMarkerTime, trackDurationSeconds);
    const clampedNewZoomLevel = Math.max(
      MIN_ZOOM_LEVEL,
      Math.min(MAX_ZOOM_LEVEL, newZoomLevel)
    );

    const lanesAreaElement = document.getElementById("lanes-area-container");
    const gridContainerElement = document.getElementById("grid-container");

    const scrollLeft = lanesAreaElement.scrollLeft;
    const viewportWidth = lanesAreaElement.clientWidth;

    // calculate scrollable areas and position at current zoom level
    const songLengthPixels = seconds_to_pixels(maxSongTime) * zoomProportion;
    const locatorX = seconds_to_pixels(locatorTime) * zoomProportion;

    const shouldFocusLocator: boolean =
      locatorX >= scrollLeft && locatorX <= scrollLeft + viewportWidth;

    const zoomCenterPixels = shouldFocusLocator
      ? locatorX
      : scrollX + viewportWidth / 2;
    const screenOffset = zoomCenterPixels - scrollLeft;
    const centerXProportion = zoomCenterPixels / songLengthPixels;

    // calculate the new values
    const newZoomProportion = getZoomProportion(clampedNewZoomLevel); // this equals 2**(clampedNewZoomLevel-1)
    const newSongLengthPixels =
      seconds_to_pixels(maxSongTime) * newZoomProportion;
    const newScrollLeftPosition = Math.min(
      Math.max(0, centerXProportion * newSongLengthPixels - screenOffset),
      newSongLengthPixels - viewportWidth
    );
    gridContainerElement.style.width = `${newSongLengthPixels}px`;
    lanesAreaElement.scrollLeft = newScrollLeftPosition;

    dispatch<ZoomChangeAction>({
      type: ZOOM_CHANGE,
      newZoomLevel: clampedNewZoomLevel
    });
  }

  /*
   * Zoom in the editor by a specific amount in [0,5]
   *
   * Parameters:
   *  increment (number): amount to zoom in
   */
  const zoomIn = (increment = 0.5) => {
    setZoomLevel(zoomLevel + increment);
  };

  /*
   * Zoom out the editor by a specific amount in [0,5]
   *
   * Parameters:
   *  decrement (number): amount to zoom out
   */
  const zoomOut = (decrement = 0.5) => {
    setZoomLevel(zoomLevel - decrement);
  };

  // recalculates horizontal scrollbar bounds on load, and when track duration changes
  useEffect(() => {
    setZoomLevel(zoomLevel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outMarkerTime, trackDurationSeconds]);

  const handleKeyDown = useCallback(
    e => {
      if (["text", "number", "textarea"].includes(e.target.type)) return;

      if (e.metaKey || e.ctrlKey) {
        // ctrl -
        if (e.key === "-") setZoomLevel(zoomLevel - 0.5);
        // ctrl +
        else if (e.key === "=" || e.key === "+") setZoomLevel(zoomLevel + 0.5);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [zoomLevel]
  );

  // create a mutable ref to handler that gets refreshed with up-to-date redux store every rerender
  // this is necessary because of useEffect dependencies & redux
  // if you add songTime to useEffect dependencies the event listeners get added & removed way too often
  const handleKeyDownRef = useRef(handleKeyDown);
  useEffect(() => {
    handleKeyDownRef.current = handleKeyDown;
  }, [handleKeyDown]);

  useEffect(() => {
    const keyDownHandler = e => handleKeyDownRef.current(e);
    if (editor.drawerState.visible) {
      document.addEventListener("keydown", keyDownHandler);
    } else {
      document.removeEventListener("keydown", keyDownHandler);
    }
    return () => document.removeEventListener("keydown", keyDownHandler);
  }, [editor.drawerState.visible]);

  const HEIGHT = 20;
  const WIDTH = HEIGHT;

  const onMouseOut = () => {
    dispatch<ZoomButtonHoverAction>({
      type: ZOOM_HOVER,
      zoomHover: false
    });
  };

  if (editor.tightLayout) {
    return (
      <ZoomContainer
        onMouseLeave={onMouseOut}
        className={`${zoomSliderVisible ? "active" : null}`}
      >
        <div
          style={{
            height: HEIGHT,
            position: "relative",
            flex: 1
          }}
        >
          <SliderRailingContainer></SliderRailingContainer>
          <Slider
            min={MIN_ZOOM_LEVEL}
            max={MAX_ZOOM_LEVEL}
            value={zoomLevel}
            step={0.01}
            onChange={onChange}
            railStyle={{
              height: HEIGHT,
              marginTop: -5, // this is to get rails aligned
              opacity: 0
            }}
            trackStyle={{
              height: HEIGHT,
              marginTop: -5,
              opacity: 0
            }}
            handleStyle={{
              height: HEIGHT,
              width: WIDTH,
              backgroundColor: theme.editor.textMedium
            }}
          />
        </div>
        <ZoomButtons>
          <div onClick={() => zoomOut()}>-</div>
          <div onClick={() => zoomIn()}>+</div>
        </ZoomButtons>
      </ZoomContainer>
    );
  } else {
    return (
      <Container
        onMouseOver={e => setZoomSliderVisible(true)}
        onMouseOut={e => setZoomSliderVisible(false)}
        className={`thicc ${zoomSliderVisible ? "active" : null}`}
      >
        <ZoomIcon className="zoom-icon">
          <Icon type="search" size="md" inheritColor />
        </ZoomIcon>
        <div
          style={{
            height: HEIGHT,
            position: "relative",
            width: 181,
            marginLeft: 18
          }}
        >
          <SliderRailingContainer></SliderRailingContainer>
          <Slider
            min={MIN_ZOOM_LEVEL}
            max={MAX_ZOOM_LEVEL}
            value={zoomLevel}
            step={0.01}
            onChange={onChange}
            railStyle={{
              height: HEIGHT,
              marginTop: -5, // this is to get rails aligned
              opacity: 0
            }}
            trackStyle={{
              height: HEIGHT,
              marginTop: -5,
              opacity: 0
            }}
            handleStyle={{
              height: HEIGHT,
              width: WIDTH,
              backgroundColor: theme.editor.textMedium
            }}
          />
        </div>
      </Container>
    );
  }
}
